import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, Platform, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Layout, RangeDatepicker, Icon, useTheme } from '@ui-kitten/components'
import { format } from 'date-fns'
import { getStatsForDays, getSpendingsOnDateRange, getProductStockSnapshot } from '../../global/Firebase/firebase'
import { getMonthlyWorkingHoursOnDateRange } from '../../global/Firebase/workingHours'
import Divider from '../../components/Divider'
import { UserContext } from '../../global/UserProvider'
import { useColors } from '../../utils/useColors'
import { useTranslation } from 'react-i18next'
import { businessDateRangeStrings, businessDayAsLocalDate, dateRangeWithTimezoneAndAutoclose, getDailyStatID, getLocalStoreDate, getStoreBusinessDate } from '../../utils/timezoneFunctions'
import { constantsAtom, currencyFormatterAtom, localeDateServiceAtom, productsDictAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { recurringSpendingAmountForRange } from '../Spendings/recurringSpendingAmount'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { exportToCSV, exportToExcel } from '../../utils/exportUtils'
import { computeStockAmountsForRange, missingStockMessageKey, stockAmountUnavailable } from './productStockAmounts'
import { dailyShiftAmounts } from './profitStatsDailyAmounts'

const getInitialRange = () => {
  let d = new Date()
  var day = d.getDay(),
  diff = d.getDate() - day + (day == 0 ? -6:1) // adjust when day is sunday
  let monday = new Date(d.setDate(diff))
  return {
    startDate: monday,
    endDate: new Date(),
  }
}

export function getOverlappingDays(dateRange1, dateRange2) {
  // Ensure the start date is less than the end date
  if (dateRange1.startDate > dateRange1.endDate || dateRange2.startDate > dateRange2.endDate) {
    return 0
  }

  // If the ranges do not overlap, return 0
  if (dateRange1.endDate < dateRange2.startDate || dateRange2.endDate < dateRange1.startDate) {
    return 0
  }

  // Calculate the overlapping days
  const overlapStart = dateRange1.startDate > dateRange2.startDate ? dateRange1.startDate : dateRange2.startDate
  const overlapEnd = dateRange1.endDate < dateRange2.endDate ? dateRange1.endDate : dateRange2.endDate

  // Calculate the difference in milliseconds and convert it to days
  const diffInMilliseconds = overlapEnd - overlapStart
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24)

  return Math.ceil(diffInDays)
}

export default function ProfitStats({ navigation, route }) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const constants = useAtomValue(constantsAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const db = context.db
  const [data, setData] = useState(null)
  const [store, setStore] = useState(context.user.store?.id)
  const [range, setRange] = useState(getInitialRange())
  const _rangePicker = React.createRef()

  useEffect(() => {
    getDataFromFB(range, store)
  }, [])

  const getDataFromFB = async (newRange, store) => {
    if (newRange.startDate != null && newRange.endDate != null) {
      newRange = dateRangeWithTimezoneAndAutoclose(newRange, (store === 'all' ? null : [store]))
      _rangePicker.current.blur()
      setData(null)

      var saleAmount = 0
      var salesPurchaseAmount = 0
      var bonusReduction = 0
      var returnsAmount = 0
      var suppliesAmount = 0 
      var writeOffsAmount = 0
      var opex0Amount = 0
      var capex2Amount = 0
      var moneyDifferenceAmount = 0
      var revisionsAmount = 0
      var correctedRevisionsAmount = 0
      var salaryAmount = 0
      var bankPercent = 0
      var transfersAmount = 0
      var manufacturesAmount = 0

      // Arrays to store daily data for export
      var salesAmountData = []
      var salesPurchaseAmountData = []
      var bonusReductionData = []
      var saleProfitData = []
      var returnsAmountData = []
      var writeOffsAmountData = []
      var revisionsAmountData = []
      var correctedRevisionsAmountData = []
      var salaryAmountData = []
      var bankPercentData = []
      var moneyDifferenceAmountData = []
      var writeOffRevisionReturnsAmountData = []
      var opex0AmountData = []
      var opexProfitData = []
      var capex2AmountData = []
      var profitData = []
      var suppliesAmountData = []
      var transfersAmountData = []
      var manufacturesAmountData = []
      var recurringSpendingsAmountData = []
      
      var salesLabels = []

      let docs = await getStatsForDays(db, newRange, store === 'all' ? null : store)
      for (let value of docs) {
        let dailySaleAmount = (value.saleAmount || 0)
        let dailySalesPurchaseAmount = (value.salesPurchaseAmount || 0)
        let dailyBonusReduction = (value.bonusReduction || 0)
        let dailyReturnsAmount = (value.returnsAmount || 0)
        let dailySuppliesAmount = (value.suppliesAmount || 0)
        let dailyWriteOffsAmount = (value.writeOffsAmount || 0)
        let dailyRevisionsAmount = (value.revisionsAmount || 0)
        let dailyCorrectedRevisionsAmount = (value.correctedRevisionsAmount || 0)
        let dailySalaryAmount = (value.salaryAmount || 0) + (value.employeeBonusAmount || 0)
        let dailyTransfersAmount = (value.transfersAmount || 0)
        let dailyManufacturesAmount = (value.manufacturesAmount || 0)
        
        // ATL-1304 — both figures come from `profitStatsDailyAmounts`, which is the same
        // arithmetic under test against the writer that produces the keys it reads.
        const { moneyDifferenceAmount: dailyMoneyDifferenceAmount, bankPercent: dailyBankPercent } =
          dailyShiftAmounts(value, constants.paymentMethods?.data)
        
        // Calculate daily profit metrics
        let dailySaleProfit = dailySaleAmount - dailySalesPurchaseAmount
        // ATL-1304: `correctedRevisionsAmount`, NOT `revisionsAmount`. The total below
        // (`writeOffRevisionReturnsAmount`) is built from the CORRECTED figure, which is also the
        // one rendered as «Разница в ревизиях», so reading the raw figure here made the per-day
        // series — and `opexProfitData`, hence the exported «Чистая прибыль» column — disagree
        // with the totals they are supposed to decompose by (raw − corrected) every day. Latent
        // while the Supabase read path served no revisions at all; live the moment it does.
        let dailyWriteOffRevisionReturnsAmount = -dailyReturnsAmount - dailyWriteOffsAmount + dailyCorrectedRevisionsAmount + dailyMoneyDifferenceAmount
        
        // Need to handle recurring spendings and opex differently since they're aggregated later
        // For now, these will be distributed evenly across days
        
        // Add to totals
        saleAmount += dailySaleAmount
        salesPurchaseAmount += dailySalesPurchaseAmount
        bonusReduction += dailyBonusReduction
        returnsAmount += dailyReturnsAmount
        suppliesAmount += dailySuppliesAmount
        writeOffsAmount += dailyWriteOffsAmount
        revisionsAmount += dailyRevisionsAmount
        correctedRevisionsAmount += dailyCorrectedRevisionsAmount
        salaryAmount += dailySalaryAmount
        moneyDifferenceAmount += dailyMoneyDifferenceAmount
        bankPercent += dailyBankPercent
        transfersAmount += dailyTransfersAmount
        manufacturesAmount += dailyManufacturesAmount
        
        // Add to daily arrays
        salesAmountData.push(dailySaleAmount)
        salesPurchaseAmountData.push(dailySalesPurchaseAmount)
        bonusReductionData.push(dailyBonusReduction)
        saleProfitData.push(dailySaleProfit)
        returnsAmountData.push(dailyReturnsAmount)
        writeOffsAmountData.push(dailyWriteOffsAmount)
        revisionsAmountData.push(dailyRevisionsAmount)
        correctedRevisionsAmountData.push(dailyCorrectedRevisionsAmount)
        salaryAmountData.push(dailySalaryAmount)
        bankPercentData.push(dailyBankPercent)
        moneyDifferenceAmountData.push(dailyMoneyDifferenceAmount)
        writeOffRevisionReturnsAmountData.push(dailyWriteOffRevisionReturnsAmount)
        suppliesAmountData.push(dailySuppliesAmount)
        transfersAmountData.push(dailyTransfersAmount)
        manufacturesAmountData.push(dailyManufacturesAmount)
        
        // Label the chart by the row's business day.
        //
        // A Firestore dailyStats doc's `date` is a mutation INSTANT, so the day it trades on has to
        // be derived (autoclose-aware) — getLocalStoreDate alone would mislabel post-midnight
        // (pre-cutoff) docs by a day (ATL-674). A Supabase row is the other case: `businessDate` IS
        // the business day, and running the instant transform over it converts twice — the date-only
        // value deserialises to UTC midnight, renders at 05:00 for a UTC+5 store, and falls before a
        // 05:30 cutoff, so all three of the production stores that close at 05:30 had every P&L
        // label booked a day early while the id beside it (ATL-1118) named the right day.
        const businessDay = businessDayAsLocalDate(value.businessDate)
        salesLabels.push(format(businessDay ?? getStoreBusinessDate((store === 'all' ? context.user.store.id : store), value.date.toDate()), 'd/M/y E'))
      }

      var salesMargin = saleAmount - salesPurchaseAmount

      // ATL-1123: opening/closing stock come from `productStockSnapshots`, which nothing wrote
      // fleet-wide between 2026-06-04 and the restore of `saveProductStockSnapshot`. The two
      // branches this replaced (one per store, one for 'all') each turned an absent document into a
      // 0 ₸ line, and the 'all' branch summed whichever stores DID have one into a total that read
      // as the whole business. `computeStockAmountsForRange` reports null plus the exact list of
      // documents it wanted instead, so the screen can say so.
      const stockAmounts = await computeStockAmountsForRange({
        statDocs: docs,
        store,
        storesDict,
        productsDict,
        loadSnapshot: (snapshotId) => getProductStockSnapshot(context.db, snapshotId),
      })
      const startProductAmount = stockAmounts.startProductAmount
      const endProductAmount = stockAmounts.endProductAmount
      const productAmountDifference = stockAmounts.productAmountDifference

      var opex0spendingsDict = {}
      var capex2spendingsDict = {}
      var opex0spendings = []
      var capex2spendings = []

      // opex0spendingsDict - {categoryName: amount}
      let spendings = await getSpendingsOnDateRange(db, newRange, true, store === 'all' ? null : store)
      for (let value of spendings) {
        // Category can be null (uncategorized) or a bare string id in older/Supabase
        // data — guard before reading .text (was: TypeError reading 'text' of null).
        const categoryName =
          typeof value.category === 'string'
            ? value.category
            : value.category?.text || value.category?.name || t('stats.noCategory')
        if (value.type == 0) {
          opex0spendingsDict[categoryName] = (opex0spendingsDict[categoryName] || 0) + value.amount
          opex0Amount += value.amount
        } else {
          capex2spendingsDict[categoryName] = (capex2spendingsDict[categoryName] || 0) + value.amount
          capex2Amount += value.amount
        }
      }

      // Fill daily opex and capex data arrays (distributing evenly across days for now)
      for (let i = 0; i < salesLabels.length; i++) {
        opex0AmountData.push(opex0Amount / salesLabels.length)
        capex2AmountData.push(capex2Amount / salesLabels.length)
      }

      // The window the report covers, in business days. Resolved once, from the same store set
      // the range was built from — see recurringSpendingAmount.js for why not per spending.
      const reportWindow = businessDateRangeStrings(newRange, store === 'all' ? null : [store])

      var recurringSpendingsAmount = 0
      var recurringSpendings = []
      for (const rS of constants.recurringSpendings.data) {
        if (store === 'all' || rS.store === store || rS.store?.id === store) {
          // ATL-1112: prorate over the report's BUSINESS DAYS; the spending's own active
          // window is classified separately, in ITS store's frame (recurringSpendingAmount.js).
          // `newRange` holds instants marking store-local day boundaries; reading their
          // calendar fields with device-local getters split one store day across two months
          // on any device outside the store's timezone, charging the day twice (one September
          // day cost 6,887.10 instead of 3,500, a full month 108,387.10 instead of 105,000).
          // NOTE: this fixes the recurring-spending line only. The monthly-SALARY block below
          // still reads `newRange` with device-local getters and has the same defect — ATL-1115,
          // deliberately out of this diff because it needs `monthlyWorkingHour.startDate/endDate`
          // to be given a meaning first.
          const amount = recurringSpendingAmountForRange(
            rS,
            reportWindow,
            store === 'all' ? null : store
          )

          recurringSpendingsAmount += amount;
          recurringSpendings.push({name: rS.name, amount: amount});
        }
      } 

      // get monthly salaries and divide them by number of days
      let monthlyWorkingHours = await getMonthlyWorkingHoursOnDateRange(newRange, store === 'all' ? null : store)
      for (let monthlyWorkingHour of monthlyWorkingHours) {
        let startDate = monthlyWorkingHour.startDate.toDate()
        let endDate = monthlyWorkingHour.endDate.toDate()
        
        // Calculate monthly salary based on actual days in each month
        let totalSalary = 0;
        
        // Is this a full month and exact month match?
        const isFullMonthMatch = 
          newRange.startDate.getDate() === 1 && // Starts on the 1st
          new Date(newRange.endDate).getDate() === new Date(newRange.endDate.getFullYear(), newRange.endDate.getMonth() + 1, 0).getDate() && // Ends on last day
          startDate.getMonth() === newRange.startDate.getMonth() && 
          startDate.getFullYear() === newRange.startDate.getFullYear() &&
          endDate.getMonth() === newRange.endDate.getMonth() && 
          endDate.getFullYear() === newRange.endDate.getFullYear();
        
        if (isFullMonthMatch) {
          // If we're viewing exactly a full month and the salary period matches, use full amount
          totalSalary = monthlyWorkingHour.amount;
        } else if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
          // If the salary period is within a single month
          const daysInMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
          const overlapDays = getOverlappingDays(newRange, {startDate, endDate});
          totalSalary = (monthlyWorkingHour.amount / daysInMonth) * overlapDays;
        } else {
          // Handle multiple months period
          let currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            
            // Calculate start and end dates for this month within the salary period
            let monthStartDate = new Date(year, month, 1);
            if (monthStartDate < startDate && month === startDate.getMonth() && year === startDate.getFullYear()) {
              monthStartDate = new Date(startDate);
            }
            
            let monthEndDate = new Date(year, month + 1, 0); // Last day of current month
            if (monthEndDate > endDate && month === endDate.getMonth() && year === endDate.getFullYear()) {
              monthEndDate = new Date(endDate);
            }
            
            // Calculate days in this portion of the salary period
            const daysInPeriod = Math.round((monthEndDate - monthStartDate) / (1000 * 60 * 60 * 24)) + 1;
            
            // Calculate overlap with the requested range
            const monthRange = {
              startDate: monthStartDate,
              endDate: monthEndDate
            };
            
            const overlapDays = getOverlappingDays(newRange, monthRange);
            
            if (overlapDays > 0) {
              // Calculate salary for this month's overlap
              totalSalary += (monthlyWorkingHour.amount / daysInMonth) * overlapDays;
            }
            
            // Move to first day of next month
            currentDate = new Date(year, month + 1, 1);
          }
        }
        
        salaryAmount += totalSalary;
      }

      for (var key in opex0spendingsDict) {
        opex0spendings.push({name: key, amount: opex0spendingsDict[key]})
      }
      for (var key in capex2spendingsDict) {
        capex2spendings.push({name: key, amount: capex2spendingsDict[key]})
      }
      var writeOffRevisionReturnsAmount = -returnsAmount - writeOffsAmount + correctedRevisionsAmount + moneyDifferenceAmount
      var opexProfit = salesMargin + writeOffRevisionReturnsAmount - salaryAmount - opex0Amount - bankPercent - recurringSpendingsAmount
      var profit = opexProfit - capex2Amount

      // Calculate daily opexProfit and profit values
      for (let i = 0; i < salesLabels.length; i++) {
        // Distribute recurring spendings evenly for now
        let dailyRecurringSpendingsAmount = recurringSpendingsAmount / salesLabels.length
        
        // Calculate daily profit metrics
        let dailyOpexProfit = saleProfitData[i] + writeOffRevisionReturnsAmountData[i] - salaryAmountData[i] - opex0AmountData[i] - bankPercentData[i] - dailyRecurringSpendingsAmount
        let dailyProfit = dailyOpexProfit - capex2AmountData[i]
        
        opexProfitData.push(dailyOpexProfit)
        profitData.push(dailyProfit)
      }

      // Add recurring spendings data (distributed evenly for now)
      for (let i = 0; i < salesLabels.length; i++) {
        recurringSpendingsAmountData.push(recurringSpendingsAmount / salesLabels.length)
      }

      setData({
        saleAmount,
        salesPurchaseAmount,
        bonusReduction,
        saleProfit: salesMargin,
        returnsAmount,
        writeOffsAmount,
        revisionsAmount,
        correctedRevisionsAmount,
        salaryAmount,
        bankPercent,
        moneyDifferenceAmount,
        writeOffRevisionReturnsAmount,
        opex0Amount,
        opexProfit,
        capex2Amount,
        profit,
        
        opex0spendings,
        capex2spendings,
        recurringSpendings,
        recurringSpendingsAmount,
        recurringSpendingsAmountData,

        suppliesAmount,
        startProductAmount,
        endProductAmount,
        productAmountDifference,
        stockAmounts,
        transfersAmount,
        manufacturesAmount,

        salesLabels,
        
        // Add daily data arrays
        salesAmountData,
        salesPurchaseAmountData,
        bonusReductionData,
        saleProfitData,
        returnsAmountData,
        writeOffsAmountData,
        revisionsAmountData,
        correctedRevisionsAmountData,
        salaryAmountData,
        bankPercentData,
        moneyDifferenceAmountData,
        writeOffRevisionReturnsAmountData,
        opex0AmountData,
        opexProfitData,
        capex2AmountData,
        profitData,
        suppliesAmountData,
        transfersAmountData,
        manufacturesAmountData,
      })
    }
  }

  /**
   * ATL-1123: a stock line whose snapshot document does not exist is UNKNOWN, not 0 ₸. Rendering it
   * as money is what let three months of absent snapshots pass for "no stock movement".
   *
   * One predicate for "this line has no figure" — imported, not restated, so the rendered line, the
   * exported cell and the copy below (`missingStockMessageKey`, which applies the same predicate to
   * decide WHICH line it names) cannot disagree about what the screen actually gave the reader.
   */
  const formatStockAmount = (amount) =>
    stockAmountUnavailable(amount) ? t('stats.stockSnapshotUnavailable') : currencyFormatter.format(amount)

  /** The same rule for CSV/Excel cells. */
  const exportStockValue = (amount) =>
    stockAmountUnavailable(amount) ? t('stats.stockSnapshotUnavailable') : amount

  /** Whether the screen shows at least one stock total, i.e. whether there is a figure to qualify. */
  const anyStockAmountShown = (stockAmounts) =>
    !!stockAmounts &&
    !(stockAmountUnavailable(stockAmounts.startProductAmount) && stockAmountUnavailable(stockAmounts.endProductAmount))

  /**
   * The stock delta as the P&L reports it: net of transfers between the org's own stores, since
   * moving goods between them is not a change in what the business holds.
   *
   * It goes through the SAME predicate as every other stock line rather than its own `=== null`
   * — a difference the reader never got must stay unavailable, and subtracting `transfersAmount`
   * from an absent one produces `NaN`, which `currencyFormatter` renders as a real-looking amount
   * and the export writes as a number. That is the fabricated figure ATL-1123 exists to remove,
   * reached from the other side.
   */
  const netProductAmountDifference = (data) =>
    stockAmountUnavailable(data.productAmountDifference)
      ? null
      : data.productAmountDifference - data.transfersAmount

  const handleExport = async (type) => {
    try {
      // Transform data into exportable format
      let exportData = data.salesLabels.map((date, index) => {
        // Get the actual day stats from Firebase (it's already in the data)
        // We'll need to retrieve daily values from the original data instead of dividing
        
        const dayData = {
          [t('date')]: date,
          // These values should be daily values, not totals divided by number of days
          [t('stats.netProductSales')]: data.salesAmountData ? data.salesAmountData[index] : data.saleAmount / data.salesLabels.length,
          [t('stats.costOfSales')]: data.salesPurchaseAmountData ? data.salesPurchaseAmountData[index] : data.salesPurchaseAmount / data.salesLabels.length,
          [t('stats.paidByBonuses')]: data.bonusReductionData ? data.bonusReductionData[index] : data.bonusReduction / data.salesLabels.length,
          [t('stats.grossIncome')]: data.saleProfitData ? data.saleProfitData[index] : data.saleProfit / data.salesLabels.length,
          [t('stats.writeOffRevisionReturnsAmount')]: data.writeOffRevisionReturnsAmountData ? data.writeOffRevisionReturnsAmountData[index] : data.writeOffRevisionReturnsAmount / data.salesLabels.length,
          [t('stats.returns')]: data.returnsAmountData ? data.returnsAmountData[index] : data.returnsAmount / data.salesLabels.length,
          [t('stats.writeOffs')]: data.writeOffsAmountData ? data.writeOffsAmountData[index] : data.writeOffsAmount / data.salesLabels.length,
          [t('stats.revisionDifferenceAmount')]: data.correctedRevisionsAmountData ? data.correctedRevisionsAmountData[index] : data.correctedRevisionsAmount / data.salesLabels.length,
          [t('stats.cashboxDifferenceAmount')]: data.moneyDifferenceAmountData ? data.moneyDifferenceAmountData[index] : data.moneyDifferenceAmount / data.salesLabels.length,
          [t('stats.salary')]: data.salaryAmountData ? data.salaryAmountData[index] : data.salaryAmount / data.salesLabels.length,
          [t('stats.bankPercent')]: data.bankPercentData ? data.bankPercentData[index] : data.bankPercent / data.salesLabels.length,
          [t('stats.spendings')]: data.opex0AmountData ? data.opex0AmountData[index] : data.opex0Amount / data.salesLabels.length,
          [t('stats.monthlySpendings')]: data.recurringSpendingsAmountData ? data.recurringSpendingsAmountData[index] : data.recurringSpendingsAmount / data.salesLabels.length,
          [t('stats.netIncome')]: data.opexProfitData ? data.opexProfitData[index] : data.opexProfit / data.salesLabels.length,
          [t('stats.capexSpendings')]: data.capex2AmountData ? data.capex2AmountData[index] : data.capex2Amount / data.salesLabels.length,
          [t('stats.pureProfit')]: data.profitData ? data.profitData[index] : data.profit / data.salesLabels.length,
          [t('stats.supplies')]: data.suppliesAmountData ? data.suppliesAmountData[index] : data.suppliesAmount / data.salesLabels.length,
          [t('stats.transfers')]: data.transfersAmountData ? data.transfersAmountData[index] : data.transfersAmount / data.salesLabels.length,
          // ATL-1123: an unavailable stock line exports as the same marker the screen shows, not
          // as 0 — a spreadsheet is exactly where a fabricated zero stops being questioned.
          [t('stats.startProductAmount')]: index === 0 ? exportStockValue(data.startProductAmount) : 0,
          [t('stats.endProductAmount')]: index === data.salesLabels.length - 1 ? exportStockValue(data.endProductAmount) : 0,
          [t('stats.productAmountDifference')]: index === data.salesLabels.length - 1
            ? exportStockValue(netProductAmountDifference(data))
            : 0,
        }
        
        // Add OPEX spendings breakdown
        data.opex0spendings.forEach(item => {
          dayData[t('stats.spendings') + ' - ' + item.name] = item.amount / data.salesLabels.length
        })
        
        // Add CAPEX spendings breakdown
        data.capex2spendings.forEach(item => {
          dayData[t('stats.capexSpendings') + ' - ' + item.name] = item.amount / data.salesLabels.length
        })
        
        // Add recurring spendings breakdown
        data.recurringSpendings.forEach(item => {
          dayData[t('stats.monthlySpendings') + ' - ' + item.name] = item.amount / data.salesLabels.length
        })
        
        return dayData
      })

      // Add totals row at the bottom
      const totalsRow = {
        [t('date')]: t('total'),
        [t('stats.netProductSales')]: data.saleAmount,
        [t('stats.costOfSales')]: data.salesPurchaseAmount,
        [t('stats.paidByBonuses')]: data.bonusReduction,
        [t('stats.grossIncome')]: data.saleProfit,
        [t('stats.writeOffRevisionReturnsAmount')]: data.writeOffRevisionReturnsAmount,
        [t('stats.returns')]: data.returnsAmount,
        [t('stats.writeOffs')]: data.writeOffsAmount,
        [t('stats.revisionDifferenceAmount')]: data.correctedRevisionsAmount,
        [t('stats.cashboxDifferenceAmount')]: data.moneyDifferenceAmount,
        [t('stats.salary')]: data.salaryAmount,
        [t('stats.bankPercent')]: data.bankPercent,
        [t('stats.spendings')]: data.opex0Amount,
        [t('stats.monthlySpendings')]: data.recurringSpendingsAmount,
        [t('stats.netIncome')]: data.opexProfit,
        [t('stats.capexSpendings')]: data.capex2Amount,
        [t('stats.pureProfit')]: data.profit,
        [t('stats.supplies')]: data.suppliesAmount,
        [t('stats.transfers')]: data.transfersAmount,
        [t('stats.startProductAmount')]: exportStockValue(data.startProductAmount),
        [t('stats.endProductAmount')]: exportStockValue(data.endProductAmount),
        [t('stats.productAmountDifference')]: exportStockValue(netProductAmountDifference(data)),
      }
      
      // Add OPEX spendings breakdown totals
      data.opex0spendings.forEach(item => {
        totalsRow[t('stats.spendings') + ' - ' + item.name] = item.amount
      })
      
      // Add CAPEX spendings breakdown totals
      data.capex2spendings.forEach(item => {
        totalsRow[t('stats.capexSpendings') + ' - ' + item.name] = item.amount
      })
      
      // Add recurring spendings breakdown totals
      data.recurringSpendings.forEach(item => {
        totalsRow[t('stats.monthlySpendings') + ' - ' + item.name] = item.amount
      })
      
      // Add the totals row to the export data
      exportData.push(totalsRow)

      const filename = 'profit-stats-' + format(range.startDate, 'd-M-y') + '-' + format(range.endDate, 'd-M-y')

      switch (type) {
        case 'csv':
          await exportToCSV(exportData, filename)
          break;
        case 'excel':
          await exportToExcel(exportData, filename)
          break;
      }
    } catch (error) {
      console.error('Export failed:', error)
      // You might want to show an error message to the user here
    }
  }

  return (    
    <ScrollView style={{padding: 20}}>
      <StoreChooserHorizontal store={store} onChange={(newStore) => {
        setStore(newStore)
        getDataFromFB(range, newStore)
      }} />
      <View style={styles.dateFilter}>
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          range={range}
          ref={_rangePicker}
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              getDataFromFB(newRange, store)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              getDataFromFB({startDate: newRange.startDate, endDate}, store)
            } else {
              setRange(newRange)
            }
          }}
        />
      </View>

      { data ? (
        <>
          <Text category='h2' style={styles.header}>
            {t('stats.pAndL')}
          </Text>
          <Layout style={styles.tableView}>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.netProductSales')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.saleAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.costOfSales')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.salesPurchaseAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>- {t('stats.paidByBonuses')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.bonusReduction)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text category='s1' style={styles.rowItemName}>{t('stats.grossIncome')}</Text>
              <Text category='s1' style={[styles.rowItemAmount, {color: colors.green}]}>{currencyFormatter.format(data.saleProfit)}</Text>
            </View>

            <Divider />
            <View style={styles.rowItem}>
              <Text category='s1' style={styles.rowItemName}>{t('stats.writeOffRevisionReturnsAmount')}</Text>
              <Text category='s1' style={[styles.rowItemAmount, {color: data.writeOffRevisionReturnsAmount > 0 ? colors.green : colors.red}]}>{currencyFormatter.format(data.writeOffRevisionReturnsAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.returns')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.returnsAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.writeOffs')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.writeOffsAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.revisionDifferenceAmount')}</Text>
              <Text style={styles.rowItemAmount}>{data.correctedRevisionsAmount > 0 ? '+' : ''}{currencyFormatter.format(data.correctedRevisionsAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.cashboxDifferenceAmount')}</Text>
              <Text style={styles.rowItemAmount}>{data.moneyDifferenceAmount > 0 ? '+' : ''}{currencyFormatter.format(data.moneyDifferenceAmount)}</Text>
            </View>

            <Divider />
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.salary')}</Text>
              <Text style={[styles.rowItemAmount, {color: colors.red}]}>{currencyFormatter.format(data.salaryAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.bankPercent')}</Text>
              <Text style={[styles.rowItemAmount, {color: colors.red}]}>{currencyFormatter.format(data.bankPercent)}</Text>
            </View>

            <Divider />
            <View style={styles.rowItem}>
              <Text category='s1' style={styles.rowItemName}>{t('stats.spendings')}</Text>
              <Text category='s1' style={[styles.rowItemAmount, {color: colors.red}]}>{currencyFormatter.format(data.opex0Amount)}</Text>
            </View>
            { data.opex0spendings.map((item, index) => (
              <View key={item+index} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{item.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(item.amount)}</Text>
              </View>
            ))}

            <Divider />
            <View style={styles.rowItem}>
              <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
                <Text category='s1'>
                  {t('stats.monthlySpendings')}
                </Text>
                { (userRole?.permissions?.['editRecurringSpendings'] || context.user.role === 'admin') && (
                  <TouchableOpacity onPress={() => navigation.navigate('UniversalList', {atom: constantsAtom, initialDataPath: ['recurringSpendings', 'data'], title: t('stats.monthlySpendings'), searchField: 'name', detailScreen: 'RecurringSpendingDetail'})} style={{marginLeft: 5, padding: 3}}>
                    <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
                  </TouchableOpacity>
                )}
              </View>
              <Text category='s1' style={[styles.rowItemAmount, {color: colors.red}]}>{currencyFormatter.format(data.recurringSpendingsAmount)}</Text>
            </View>
            { data.recurringSpendings.map((item, index) => (
              <View style={styles.rowItem} key={item+index}>
                <Text style={styles.rowItemName}>{item.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(item.amount)}</Text>
              </View>
            ))}
            <Divider />
            <View style={styles.rowItem}>
              <Text category='h6' style={styles.rowItemName}>{t('stats.netIncome')}</Text>
              <Text category='h6' style={[styles.rowItemAmount, {color: data.opexProfit > 0 ? colors.green : colors.red}]}>{currencyFormatter.format(data.opexProfit)}</Text>
            </View>
          
            <Divider />
            <View style={styles.rowItem}>
              <Text category='s1' style={styles.rowItemName}>{t('stats.capexSpendings')}</Text>
              <Text category='s1' style={[styles.rowItemAmount, {color: colors.red}]}>{currencyFormatter.format(data.capex2Amount)}</Text>
            </View>
            { data.capex2spendings.map((item, index) => (
              <View style={styles.rowItem} key={item+index}>
                <Text style={styles.rowItemName}>{item.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(item.amount)}</Text>
              </View>
            ))}
            <Divider />
            <View style={styles.rowItem}>
              <Text category='h6' style={styles.rowItemName}>{t('stats.pureProfit')}</Text>
              <Text category='h6'style={[styles.rowItemAmount, {color: data.profit > 0 ? colors.green : colors.red}]}>{currencyFormatter.format(data.profit)}</Text>
            </View>
            <Divider />
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.supplies')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.suppliesAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.transfers')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.transfersAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.startProductAmount')}</Text>
              <Text style={styles.rowItemAmount}>{formatStockAmount(data.startProductAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.endProductAmount')}</Text>
              <Text style={styles.rowItemAmount}>{formatStockAmount(data.endProductAmount)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text category='s1' style={styles.rowItemName}>{t('stats.productAmountDifference')}</Text>
              <Text category='s1' style={styles.rowItemAmount}>{formatStockAmount(netProductAmountDifference(data))}</Text>
            </View>
            {/* Only when documents were actually wanted and not found: `complete` is also false for
                a range with no daily stats at all, where the count would render as a literal 0.
                Which sentence — and why the entries that are NOT a day/store pair get a different
                one — is `missingStockMessageKey`, next to the rule that decides what was withheld
                (ATL-1279: since a missing boundary withholds only its own amount, the sentence has
                to name the line that is actually absent). */}
            { data.stockAmounts && data.stockAmounts.missing.length > 0 && (
              <View style={styles.rowItem}>
                <Text style={[styles.rowItemName, {color: colors.red}]}>
                  {t(missingStockMessageKey(data.stockAmounts), {count: data.stockAmounts.missing.length})}
                </Text>
              </View>
            )}
            {/* An unknown cell is valued at 0, so a snapshot that is present but partly unknown
                UNDERSTATES the total — silently, and with `complete` still true. That is the same
                fabricated figure this ticket exists to remove, one layer in, and it is the shape a
                reconstructed snapshot takes (every refused cell is written as the unknown marker).
                Say how many rather than letting the number pass for the whole valuation.
                Only when a total is actually SHOWN, so the sentence cannot appear under two lines
                reading "no data" — a claim about a number nobody was given. ATL-1279 made
                `unknownCellCount` itself count only the boundaries that were reported, so the guard
                is belt-and-braces rather than the rule: it is what keeps this line honest if that
                reducer is ever loosened back to a running total. */}
            { anyStockAmountShown(data.stockAmounts) && data.stockAmounts.unknownCellCount > 0 && (
              <View style={styles.rowItem}>
                <Text style={[styles.rowItemName, {color: colors.red}]}>
                  {t('stats.stockSnapshotUnknownCells', {count: data.stockAmounts.unknownCellCount})}
                </Text>
              </View>
            )}
            {/* ATL-1281: the sentence above admits the two TOTALS are understated; it says nothing
                about the DIFFERENCE, which is the line a merchant reads as stock movement. A
                product priced at zero on one boundary and valued on the other moves that delta by
                its whole stock with nothing bought or sold — reconstructed opening beside a live
                closing does exactly that (uV2Urt7u6YuXDRu2TBge: 4,986,651 → 6,715,452 overnight,
                140 refused cells against 7). Only when a difference is actually on screen: with no
                number there is nothing to qualify. */}
            { data.stockAmounts && data.stockAmounts.differenceComparable === false && (
              <View style={styles.rowItem}>
                <Text style={[styles.rowItemName, {color: colors.red}]}>
                  {t('stats.stockDifferenceNotComparable', {count: data.stockAmounts.asymmetricUnknownCount})}
                </Text>
              </View>
            )}
            <Divider />
            <View style={{height: 50}}></View>
          </Layout>
          
          { data && (userRole?.permissions?.['exportImport'] || userRole?.id === 'admin') && (
            <>
              <Button appearance="ghost" status='basic' onPress={() => handleExport('csv')}>{t('csvExport')}</Button>
              <Button appearance="ghost" status='basic' onPress={() => handleExport('excel')}>{t('excelExport')}</Button>
            </>
          )}
        </>
      ) : (
        <View style={styles.spinner}>
          <Spinner size="large" />
        </View>
      )}

      
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  header: {
    marginVertical: 10,
  },
  tableView: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
  }, 
  rowItemName: {
    flex: 9
  }, 
  rowItemAmount: {
    flex: 4,
    textAlign: 'right',
  },
  label: {
    marginTop: 5,
    marginBottom: 5,
  },
  spinner: {
    marginTop: 20,
    justifyContent: 'center',
    alignItems: 'center'
  },
})